import React, { useMemo } from "react";
import { Button, Input, Radio, Select } from "antd";
import { CloseCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { useGetEntityMetadataQuery } from "../../../../Redux/service/Forms/FormEntities";
import trans from "../../../../utils";

const ARITHMETIC_OPERATORS = [
	{ label: trans("Add"), value: "add" },
	{ label: trans("Subtract"), value: "subtract" },
	{ label: trans("Multiply"), value: "multiply" },
	{ label: trans("Divide"), value: "divide" },
];

const FORMULA_OPERATORS = [
	...ARITHMETIC_OPERATORS,
	{ label: trans("Sum"), value: "sum" },
	{ label: trans("Average"), value: "avg" },
	{ label: trans("Minimum"), value: "min" },
	{ label: trans("Maximum"), value: "max" },
];

export default function FormOptionDerived({ activeItem, siblingFields, updateActiveItem }) {
	const config = activeItem?.derivedConfig || {};
	const mode = config.mode || "entity";
	const sourceField = useMemo(
		() => siblingFields.find((field) => field.key === config.sourceField),
		[siblingFields, config.sourceField]
	);
	const entityKey = sourceField?.dropdownOptions?.entityConfig?.entityKey;
	const { data: metadata } = useGetEntityMetadataQuery(entityKey, { skip: !entityKey });
	const availableFields = metadata?.data?.availableFields || [];

	const sourceFieldOptions = siblingFields
		.filter(
			(field) =>
				(field.name === "Dropdown" || field.name === "MultiSelect") &&
				field.dropdownOptions?.entityConfig?.entityKey
		)
		.map((field) => ({ label: field.title || field.key, value: field.key }));
	const numericFieldOptions = siblingFields
		.filter(
			(field) =>
				(field.name === "Input" && field.inputType === "number") ||
				field.name === "DerivedValue" ||
				field.name === "ComputedValue"
		)
		.map((field) => ({ label: field.title || field.key, value: field.key }));
	const groupOptions = siblingFields
		.filter((field) => field.name === "RepeatingGroup")
		.map((field) => ({ label: field.title || field.key, value: field.key }));
	const selectedGroup = siblingFields.find((field) => field.key === config.groupField);
	const groupChildOptions = (selectedGroup?.fields || [])
		.filter(
			(field) =>
				(field.name === "Input" && field.inputType === "number") ||
				field.name === "DerivedValue" ||
				field.name === "ComputedValue"
		)
		.map((field) => ({ label: field.title || field.key, value: field.key }));
	const operands = Array.isArray(config.operands) ? config.operands : [];

	const hasDefaultValue =
		config.defaultValueOptions ||
		(config.defaultValue !== undefined &&
			config.defaultValue !== null &&
			String(config.defaultValue).trim() !== "");

	const defaultValueSource = config.defaultValueOptions ? "dropdown" : "manual";
	const staticOptions = Array.isArray(config.defaultValueOptions?.data)
		? config.defaultValueOptions.data
		: [];

	const setDefaultValueSource = (source) => {
		if (source === "manual") {
			updateActiveItem("derivedConfig", {
				...config,
				defaultValue: config.defaultValue,
				defaultValueOptions: undefined,
			});
		} else {
			updateActiveItem("derivedConfig", {
				...config,
				defaultValue: null,
				defaultValueOptions: { queryKey: "static", data: [] },
			});
		}
	};

	const updateStaticOption = (index, value) => {
		const next = staticOptions.map((opt, i) => (i === index ? value : opt));
		updateActiveItem("derivedConfig.defaultValueOptions.data", next);
	};

	const addStaticOption = () => {
		updateActiveItem("derivedConfig.defaultValueOptions.data", [...staticOptions, ""]);
	};

	const removeStaticOption = (index) => {
		updateActiveItem(
			"derivedConfig.defaultValueOptions.data",
			staticOptions.filter((_, i) => i !== index)
		);
	};

	const changeMode = (nextMode) => {
		updateActiveItem("derivedConfig", { mode: nextMode });
	};
	const updateOperands = (nextOperands) =>
		updateActiveItem("derivedConfig.operands", nextOperands);

	return (
		<div className="field-settings-section">
			<h5 className="field-settings-section-title">{trans("Derived Value Configuration")}</h5>

			<div className="field-settings-control">
				<label className="form-label">{trans("Default Value Source")}</label>
				<Radio.Group
					style={{ display: "flex", gap: 16 }}
					value={defaultValueSource}
					onChange={(e) => setDefaultValueSource(e.target.value)}
					options={[
						{ label: trans("Manual"), value: "manual" },
						{ label: trans("Dropdown"), value: "dropdown" },
					]}
				/>
			</div>

			{defaultValueSource === "manual" && (
				<div className="field-settings-control">
					<label className="form-label">{trans("Default Value")}</label>
					<Input
						style={{ width: "100%" }}
						value={config.defaultValue ?? ""}
						onChange={(e) =>
							updateActiveItem("derivedConfig.defaultValue", e.target.value)
						}
						placeholder={trans("Constant value used when set; otherwise a fallback")}
						allowClear
						onBlur={(e) => {
							const raw = e.target.value;
							const numeric =
								activeItem?.dataType === "number" ||
								activeItem?.inputType === "number";
							if (numeric && raw !== "" && !Number.isNaN(Number(raw))) {
								updateActiveItem("derivedConfig.defaultValue", Number(raw));
							} else if (raw === "") {
								updateActiveItem("derivedConfig.defaultValue", null);
							}
						}}
					/>
				</div>
			)}

			{defaultValueSource === "dropdown" && (
				<div className="field-settings-control">
					<label className="form-label">{trans("Default Value Options")}</label>

					<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
						{staticOptions.map((option, index) => (
							<div key={index} className="field-settings-list-row">
								<Input
									style={{ flex: 1 }}
									placeholder={trans("Value")}
									value={option || ""}
									onChange={(e) =>
										updateStaticOption(index, e.target.value)
									}
								/>
								<Button
									type="text"
									danger
									icon={<CloseCircleOutlined />}
									onClick={() => removeStaticOption(index)}
								/>
							</div>
						))}
						<Button type="dashed" block icon={<PlusOutlined />} onClick={addStaticOption}>
							{trans("Add Option")}
						</Button>
					</div>

					<div className="field-settings-control" style={{ marginTop: 12, marginBottom: 0 }}>
						<label className="form-label">{trans("Selected Default Value")}</label>
						<Select
							style={{ width: "100%" }}
							value={config.defaultValue ?? null}
							options={staticOptions
								.filter((opt) => opt !== "")
								.map((opt) => ({ label: String(opt), value: opt }))}
							onChange={(value) => updateActiveItem("derivedConfig.defaultValue", value)}
							placeholder={trans("Select a default value")}
							allowClear
							onClear={() => updateActiveItem("derivedConfig.defaultValue", null)}
						/>
					</div>
				</div>
			)}

			{hasDefaultValue && (
				<div
					style={{
						padding: "8px 10px",
						borderRadius: 6,
						background: "#eff6ff",
						color: "#1e40af",
						fontSize: 12,
						marginBottom: 12,
					}}
				>
					{trans(
						"A default value is set, so the derived value is treated as a constant and configuration below is disabled."
					)}
				</div>
			)}

			<div className="field-settings-control">
				<label className="form-label">{trans("Value Source")}</label>
				<Select
					style={{ width: "100%" }}
					value={mode}
					onChange={changeMode}
					disabled={hasDefaultValue}
					options={[
						{ label: trans("Entity Property"), value: "entity" },
						{ label: trans("Field Formula"), value: "formula" },
						{ label: trans("Group Summary"), value: "aggregate" },
					]}
				/>
			</div>

			{mode === "entity" && !hasDefaultValue && (
				<>
					<div className="field-settings-control">
						<label className="form-label">{trans("Source Field")}</label>
						<Select
							style={{ width: "100%" }}
							value={config.sourceField}
							options={sourceFieldOptions}
							onChange={(sourceFieldKey) => {
								updateActiveItem("derivedConfig.sourceField", sourceFieldKey);
								updateActiveItem("derivedConfig.sourceProperty", null);
							}}
							placeholder={trans("Select a source field")}
							allowClear
						/>
					</div>
					<div className="field-settings-control">
						<label className="form-label">{trans("Source Property")}</label>
						<Select
							style={{ width: "100%" }}
							value={config.sourceProperty}
							options={availableFields.map((field) => ({
								label: field.label || field.key,
								value: field.key,
							}))}
							onChange={(value) =>
								updateActiveItem("derivedConfig.sourceProperty", value)
							}
							disabled={!entityKey}
							allowClear
						/>
					</div>
					<div className="field-settings-control">
						<label className="form-label">{trans("Optional Operator")}</label>
						<Select
							style={{ width: "100%" }}
							value={config.calculation?.operator}
							options={ARITHMETIC_OPERATORS}
							onChange={(operator) =>
								updateActiveItem("derivedConfig.calculation", {
									...config.calculation,
									operator,
								})
							}
							allowClear
							onClear={() => updateActiveItem("derivedConfig.calculation", null)}
						/>
					</div>
					<div className="field-settings-control">
						<label className="form-label">{trans("Number Field")}</label>
						<Select
							style={{ width: "100%" }}
							value={config.calculation?.field}
							options={numericFieldOptions}
							onChange={(field) =>
								updateActiveItem("derivedConfig.calculation", {
									...config.calculation,
									field,
								})
							}
							disabled={!config.calculation?.operator}
							allowClear
						/>
					</div>
				</>
			)}

			{mode === "formula" && !hasDefaultValue && (
				<>
					<div className="field-settings-control">
						<label className="form-label">{trans("Operator")}</label>
						<Select
							style={{ width: "100%" }}
							value={config.operator}
							options={FORMULA_OPERATORS}
							onChange={(operator) => updateActiveItem("derivedConfig.operator", operator)}
						/>
					</div>
					<label className="form-label">{trans("Fields")}</label>
					{operands.map((operand, index) => (
						<div key={index} className="field-settings-list-row">
							<Select
								style={{ flex: 1 }}
								value={operand}
								options={numericFieldOptions}
								onChange={(value) => {
									const next = [...operands];
									next[index] = value;
									updateOperands(next);
								}}
							/>
							<Button
								className="field-settings-icon-button"
								type="text"
								danger
								icon={<CloseCircleOutlined />}
								onClick={() => updateOperands(operands.filter((_, i) => i !== index))}
							/>
						</div>
					))}
					<Button type="dashed" block onClick={() => updateOperands([...operands, null])}>
						{trans("Add Field")}
					</Button>
				</>
			)}

			{mode === "aggregate" && !hasDefaultValue && (
				<>
					<div className="field-settings-control">
						<label className="form-label">{trans("Repeating Group")}</label>
						<Select
							style={{ width: "100%" }}
							value={config.groupField}
							options={groupOptions}
							onChange={(groupField) => {
								updateActiveItem("derivedConfig.groupField", groupField);
								updateActiveItem("derivedConfig.childField", null);
							}}
						/>
					</div>
					<div className="field-settings-control">
						<label className="form-label">{trans("Group Number Field")}</label>
						<Select
							style={{ width: "100%" }}
							value={config.childField}
							options={groupChildOptions}
							onChange={(childField) =>
								updateActiveItem("derivedConfig.childField", childField)
							}
							disabled={!config.groupField}
						/>
					</div>
					<div className="field-settings-control">
						<label className="form-label">{trans("Summary")}</label>
						<Select
							style={{ width: "100%" }}
							value={config.operator || "sum"}
							options={FORMULA_OPERATORS.filter((option) =>
								["sum", "avg", "min", "max"].includes(option.value)
							)}
							onChange={(operator) => updateActiveItem("derivedConfig.operator", operator)}
						/>
					</div>
				</>
			)}
		</div>
	);
}
